import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Switch, Route, Redirect } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useFirebase, useFirestore } from 'react-redux-firebase';
import Login from '../screens/Login';
import SignUp from '../screens/SignUp';
import RecoverPassword from '../screens/RecoverPassword';
import TemplateMessages from '../screens/Templates/TemplateMessages';
import ActiveChats from '../screens/Chats/ActiveChats';
import Organizations from '../screens/Organizations/Organizations';
import Users from '../screens/Users';
import PrivateRoute from './PrivateRoute';
import { Box, Snackbar } from '@mui/material';
import {
  CHATS,
  LOGIN,
  RECOVER_PASSWORD,
  SIGNUP,
  USERS,
  MESSAGES,
  CHANNELS,
  TEMPLATES,
  ORGANIZATIONS,
  CONFIGURATION,
  NOTIFICATIONS,
  TRAINING,
  CLIENT_CONFIGURATION,
  PRIVACY,
  REMINDERS,
  SPECIALTIES,
  TEMPLATES_WAPI,
  PATIENT_REMINDERS,
  ONBOARDING,
  PRIVACY_POLICY,
  DASHBOARD,
} from '../constants/routes';
import Channels from '../screens/Channels';
import Messages from '../screens/Messages/Messages';
import Configuration from '../screens/Configuration/Configuration';
import { actions } from '../actions';
import WithNavbar from './WithNavbar';
import Notifications from '../screens/Notifications/Notifications';
import Training from '../screens/Training/Training';
import Specialties from '../screens/Specialties/Specialties';
import WithHeader from './WithHeader';
import {
  Group,
  PermDeviceInformation,
  PlaylistAddCheck,
  RocketLaunch,
  Settings,
} from '@mui/icons-material';
import Privacy from '../screens/Privacy';
import PrivacyPolicy from '../screens/PrivacyPolicy';
import TemplatesWapi from '../screens/TemplatesWapi';
import Dashboard from '../screens/Dashboard/Dashboard';
import Reminders from '../screens/Reminders/Reminders';
import LoadingSpinner from '../shared/LoadingSpinner/LoadingSpinner';
import Alert from '@mui/material/Alert';
import collections from '../constants/FirestoreCollections';
import ClientConfiguration from '../screens/ClientConfiguration/ClientConfiguration';
import PatientReminders from '../screens/PatientReminders';
import Prepaid from '../screens/Dashboard/Prepaid';
import Price from '../screens/Dashboard/Price';
import Graphics from '../screens/Dashboard/Graphics';
import NavTabs from '../screens/Dashboard/components/NavigateTab';
import Onboarding from '../screens/Onboarding/Onboarding';
import { handleErrorSwal } from '../shared/swals';
import DFMessengerChatBubble from '../shared/CustomDFMessengerChat/DFMessengerChatBubble';

export default function AppRouter() {
  const firebase = useFirebase();
  const firestore = useFirestore();
  const [isSmall, setIsSmall] = useState(false);
  const appDispatch = useDispatch();
  const alert = useSelector((state) => state.app.alert);
  const loadingConfig = useSelector((state) => state.app.loading);
  const profile = useSelector((state) => state.firebase.profile);
  const organization = useSelector((state) => state.app.organization);

  const handleSnackbarClose = () => {
    appDispatch({ type: actions.alert, alert: { snackbarOpen: false } });
  };

  useEffect(() => {
    let unsubscribe;
    if (profile.organizationId) {
      unsubscribe = firestore
        .collection(collections.messages)
        .where('organizationId', '==', profile.organizationId)
        .onSnapshot((snapshot) => {
          const messages = [];
          snapshot.forEach((e) =>
            messages.push({
              id: e.id,
              ...e.data(),
            }),
          );
          appDispatch({
            type: actions.messages,
            messages,
          });
        });
    }
    return () => {
      unsubscribe && unsubscribe();
    };
  }, [appDispatch, firestore, profile.organizationId]);

  useEffect(() => {
    if (profile.organizationId) {
      const unsubscribe = firestore
        .collection(collections.organizations)
        .doc(profile.organizationId)
        .onSnapshot((snapshot) => {
          appDispatch({
            type: actions.organization,
            organization: { ...snapshot.data(), id: snapshot.id },
          });
        });
      return () => {
        unsubscribe && unsubscribe();
      };
    }
  }, [appDispatch, firestore, profile.organizationId]);

  useEffect(() => {
    if (profile.organizationId) {
      firestore
        .collection(collections.organizations)
        .get()
        .then((snapshot) => {
          const organizations = [];
          snapshot.forEach((e) => organizations.push({ id: e.id, ...e.data() }));
          appDispatch({
            type: actions.organizations,
            organizations,
          });
        });
    }
  }, [appDispatch, firestore, profile.organizationId]);

  useEffect(() => {
    if (organization && organization.id) {
      firestore
        .collection(collections.configurations)
        .where('name', '==', 'API')
        .where('organizationId', '==', organization.id)
        .get()
        .then((res) => {
          if (!res.empty) {
            const configAPI = { id: res.docs[0].id, ...res.docs[0].data() };
            appDispatch({
              type: actions.configAPI,
              configAPI,
            });
          } else {
            handleErrorSwal(
              'Error al cargar la configuración de la API. Por favor, recargue la página. Si el problema persiste, contacte a nuestro equipo de soporte.',
            );
          }
        });
    }
  }, [firestore, organization.id]);

  const updateDimensions = () => {
    // if (!isSmall && window.innerWidth < 1000) setIsSmall(true);
    if (isSmall && window.innerWidth >= 1000) setIsSmall(false);
  };

  useEffect(() => {
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [isSmall]);

  useEffect(() => {
    if (profile.organizationId) {
      firestore
        .collection(collections.configurations)
        .where('name', '==', 'labels')
        .get()
        .then((res) => {
          if (!res.empty) {
            const config = res && res.docs && res.docs[0] && res.docs[0].data();
            appDispatch({
              type: actions.labelsConfiguration,
              labelsConfiguration: config,
            });
          }
        });
    }
  }, [appDispatch, firebase, firestore, profile.organizationId]);

  useEffect(() => {
    if (organization.id) {
      firestore
        .collection(collections.configurations)
        .where('organizationId', '==', organization.id)
        .where('name', '==', 'notifications')
        .get()
        .then((res) => {
          if (!res.empty) {
            const config = res.docs[0].data();
            appDispatch({
              type: actions.templatesSent,
              templates: config.templatesSent || [],
            });
          }
        });
    }
  }, [appDispatch, firebase, firestore, organization.id]);

  useEffect(() => {
    if (!loadingConfig && organization.id && profile.organizationId !== organization.id) {
      appDispatch({
        type: actions.changeLoading,
        loading: { loading: true, priority: 1 },
      });
    } else if (loadingConfig && organization.id && profile.organizationId === organization.id) {
      appDispatch({
        type: actions.changeLoading,
        loading: { loading: false, priority: 1 },
      });
    }
  }, [appDispatch, organization.id, profile.organizationId]);

  return !profile || !profile.isLoaded ? null : (
    <Box>
      {loadingConfig && loadingConfig.loading && <LoadingSpinner text={loadingConfig.text} />}
      <Router>
        <Switch>
          <PrivateRoute exact path="/">
            <Redirect to={CHATS} />
          </PrivateRoute>
          <PrivateRoute exact path={DASHBOARD} admin organizationAdmin>
            <WithNavbar>
              <NavTabs />
              <Dashboard />
            </WithNavbar>
          </PrivateRoute>
          <PrivateRoute exact path={`${DASHBOARD}/prepaid`} admin>
            <WithNavbar>
              <NavTabs />
              <Prepaid />
            </WithNavbar>
          </PrivateRoute>
          <PrivateRoute exact path={`${DASHBOARD}/prices`} admin>
            <WithNavbar>
              <NavTabs />
              <Price />
            </WithNavbar>
          </PrivateRoute>
          <PrivateRoute exact path={`${DASHBOARD}/graphics`} admin organizationAdmin>
            <WithNavbar>
              <NavTabs />
              <Graphics />
            </WithNavbar>
          </PrivateRoute>
          <PrivateRoute path={CHATS}>
            <WithNavbar>
              <ActiveChats />
            </WithNavbar>
          </PrivateRoute>
          <PrivateRoute path={MESSAGES} admin organizationAdmin>
            <WithNavbar>
              <Messages />
            </WithNavbar>
          </PrivateRoute>
          <PrivateRoute path={USERS} admin organizationAdmin>
            <WithNavbar>
              <WithHeader title="Users" label="users" Icon={<Group />}>
                <Users />
              </WithHeader>
            </WithNavbar>
          </PrivateRoute>
          <Route path={LOGIN}>
            <Login />
          </Route>
          <Route path={PRIVACY}>
            <Privacy />
          </Route>
          <Route path={PRIVACY_POLICY}>
            <PrivacyPolicy />
          </Route>
          <Route path={PATIENT_REMINDERS}>
            <PatientReminders />
          </Route>
          <Route path={TEMPLATES_WAPI}>
            <TemplatesWapi />
          </Route>
          <Route path={RECOVER_PASSWORD}>
            <RecoverPassword />
          </Route>
          <PrivateRoute path={CHANNELS} admin organizationAdmin>
            <WithNavbar>
              <WithHeader title="Configuración" label="webchat" Icon={<Settings />}>
                <Channels />
              </WithHeader>
            </WithNavbar>
          </PrivateRoute>
          <PrivateRoute path={TEMPLATES} admin organizationAdmin>
            <WithNavbar>
              <TemplateMessages />
            </WithNavbar>
          </PrivateRoute>
          <PrivateRoute path={CONFIGURATION} admin>
            <WithNavbar>
              <WithHeader title="Configuración" label="webchat" Icon={<Settings />}>
                <Configuration />
              </WithHeader>
            </WithNavbar>
          </PrivateRoute>
          <PrivateRoute path={SPECIALTIES} admin>
            <WithNavbar>
              <WithHeader title="Especialidades" label="specialties" Icon={<PlaylistAddCheck />}>
                <Specialties />
              </WithHeader>
            </WithNavbar>
          </PrivateRoute>
          <PrivateRoute path={CLIENT_CONFIGURATION} admin organizationAdmin>
            <WithNavbar>
              <WithHeader title="Configuración" Icon={<Settings />}>
                <ClientConfiguration />
              </WithHeader>
            </WithNavbar>
          </PrivateRoute>
          <PrivateRoute path={REMINDERS} admin organizationAdmin>
            <WithNavbar>
              <WithHeader
                title="Filtros de recordatorios"
                label="reminders"
                Icon={<PlaylistAddCheck />}
              >
                <Reminders />
              </WithHeader>
            </WithNavbar>
          </PrivateRoute>
          <PrivateRoute path={TRAINING} admin>
            <WithNavbar>
              <Training />
            </WithNavbar>
          </PrivateRoute>
          <PrivateRoute path={ONBOARDING} admin>
            <WithNavbar>
              <WithHeader title="Onboarding" Icon={<RocketLaunch />}>
                <Onboarding />
              </WithHeader>
            </WithNavbar>
          </PrivateRoute>
          <PrivateRoute path={NOTIFICATIONS} admin organizationAdmin>
            <WithNavbar>
              <WithHeader title="Notificaciones" label="" Icon={<PermDeviceInformation />}>
                <Notifications />
              </WithHeader>
            </WithNavbar>
          </PrivateRoute>
          {/* CLIENTES */}
          <PrivateRoute path={ORGANIZATIONS} admin>
            <WithNavbar>
              <Organizations />
            </WithNavbar>
          </PrivateRoute>
          <Route path={SIGNUP}>
            <SignUp />
          </Route>
        </Switch>
        <DFMessengerChatBubble />
      </Router>
      <Snackbar
        sx={{ zIndex: 10000 }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={alert.snackbarOpen}
        onClose={handleSnackbarClose}
        autoHideDuration={alert.hideDuration}
      >
        <Alert onClose={handleSnackbarClose} severity={alert.snackbarSeverity}>
          {alert.snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
